/**
 * Route Prices List Page Component
 * /admin/route-prices
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import apiClient from '../api/client';
import { exportToCSV } from '../utils/export';

const RoutePrices = () => {
  const [routePrices, setRoutePrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [priceListFilter, setPriceListFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [priceLists, setPriceLists] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if price_list filter is in URL params
    const priceListParam = searchParams.get('price_list');
    if (priceListParam) {
      setPriceListFilter(priceListParam);
    }
    loadPriceLists();
    loadRoutePrices();
  }, [priceListFilter, departmentFilter, activeFilter]);

  const loadPriceLists = async () => {
    try {
      const response = await apiClient.getCorporatePriceLists({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setPriceLists(data);
    } catch (err) {
      console.error('Failed to load price lists:', err);
    }
  };

  const loadRoutePrices = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (priceListFilter) params.price_list = priceListFilter;
      if (departmentFilter) params.department = departmentFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';
      if (searchTerm) params.search = searchTerm;
      
      const response = await apiClient.getRoutePrices(params);
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setRoutePrices(data);
    } catch (err) {
      setError(err.message || 'Failed to load route prices');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadRoutePrices();
  };

  const handleDelete = async (id, pickup, dropoff) => {
    if (!window.confirm(`Are you sure you want to delete route price "${pickup} → ${dropoff}"?`)) {
      return;
    }
    try {
      await apiClient.deleteRoutePrice(id);
      setError('');
      loadRoutePrices();
    } catch (err) {
      setError(err.message || 'Failed to delete route price');
    }
  };

  if (loading && routePrices.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Route Prices
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => {
              const headers = [
                { key: 'price_list_name', label: 'Price List' },
                { key: 'pickup_location.name', label: 'Pickup Location' },
                { key: 'dropoff_location.name', label: 'Dropoff Location' },
                { key: 'base_price', label: 'Base Price' },
                { key: 'department', label: 'Department' },
                { key: 'cost_center_code', label: 'Cost Center' },
                { key: 'priority', label: 'Priority' },
                { key: 'is_active', label: 'Active' },
              ];
              exportToCSV(routePrices, 'route-prices', headers);
            }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/route-prices/new')}
          >
            New Route Price
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <form onSubmit={handleSearchSubmit} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              placeholder="Search route prices..."
              value={searchTerm}
              onChange={handleSearch}
              slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              } }}
            />
          </form>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Price List</InputLabel>
            <Select
              value={priceListFilter}
              label="Price List"
              onChange={(e) => setPriceListFilter(e.target.value)}
            >
              <MenuItem value="">All Price Lists</MenuItem>
              {priceLists.map((priceList) => (
                <MenuItem key={priceList.id} value={priceList.id}>
                  {priceList.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={departmentFilter}
              label="Department"
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <MenuItem value="">All Departments</MenuItem>
              <MenuItem value="operations">Operations</MenuItem>
              <MenuItem value="sales">Sales</MenuItem>
              <MenuItem value="management">Management</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              label="Status"
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Price List</TableCell>
              <TableCell>Pickup Location</TableCell>
              <TableCell>Dropoff Location</TableCell>
              <TableCell>Base Price</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Cost Center</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {routePrices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No route prices found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              routePrices.map((routePrice) => (
                <TableRow key={routePrice.id} hover>
                  <TableCell>{routePrice.price_list_name || '-'}</TableCell>
                  <TableCell>
                    {routePrice.pickup_location?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {routePrice.dropoff_location?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {routePrice.base_price ? `AED ${parseFloat(routePrice.base_price).toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>{routePrice.department || '-'}</TableCell>
                  <TableCell>{routePrice.cost_center_code || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={routePrice.priority || 'normal'}
                      size="small"
                      color={routePrice.priority === 'high' ? 'error' : routePrice.priority === 'medium' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={routePrice.is_active ? 'Active' : 'Inactive'}
                      color={routePrice.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/route-prices/${routePrice.id}`)}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/route-prices/${routePrice.id}/edit`)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(
                        routePrice.id,
                        routePrice.pickup_location?.name || 'Unknown',
                        routePrice.dropoff_location?.name || 'Unknown'
                      )}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default RoutePrices;

